import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLeadLists } from "@/hooks/use-lead-lists";
import { useLeadRules, useSetLeadQueueSettings, type BookerQueue } from "@/hooks/use-lead-queue";

interface Props {
  booker: BookerQueue | null;
  onClose: () => void;
}

const toNumber = (v: string) => (v.trim() === "" ? null : Number(v));

/** «Bookerens kø og valg» (VIB-3050, skjerm 4): lavvann, høyvann og hvilke pooler køen fylles fra. */
export default function LeadQueueSettingsDialog({ booker, onClose }: Props) {
  const { data: lists = [] } = useLeadLists({ enabled: !!booker });
  const { data: rules } = useLeadRules({ enabled: !!booker });
  const save = useSetLeadQueueSettings();
  const pools = lists.filter((l) => l.owner_id === null && l.is_active);
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const [allPools, setAllPools] = useState(true);
  const [chosen, setChosen] = useState<string[]>([]);

  useEffect(() => {
    if (!booker) return;
    setLow(booker.low_overridden ? String(booker.low_water) : "");
    setHigh(booker.high_overridden ? String(booker.high_water) : "");
    setAllPools(booker.pool_ids === null);
    setChosen(booker.pool_ids ?? []);
  }, [booker]);

  const lowN = toNumber(low) ?? rules?.queue_low ?? 10;
  const highN = toNumber(high) ?? rules?.queue_high ?? 30;
  const valid = Number.isInteger(lowN) && Number.isInteger(highN) && lowN >= 0 && highN <= 200 && lowN < highN;

  return (
    <Dialog open={!!booker} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{booker?.full_name}</DialogTitle>
          <DialogDescription>
            Køen nå: {booker?.callable ?? 0} ringbare. Hva som havner i en pool, bestemmes der leadene hentes inn. Her velger du bare hvilke pooler
            køen fylles fra.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!booker || !valid) return;
            save.mutate(
              { userId: booker.user_id, low: toNumber(low), high: toNumber(high), poolIds: allPools ? null : chosen },
              { onSuccess: onClose },
            );
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="queue-low">Lavvann</Label>
              <Input id="queue-low" type="number" min={0} value={low} placeholder={String(rules?.queue_low ?? 10)} onChange={(e) => setLow(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="queue-high">Høyvann</Label>
              <Input id="queue-high" type="number" min={1} max={200} value={high} placeholder={String(rules?.queue_high ?? 30)} onChange={(e) => setHigh(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Tomt felt bruker standarden fra reglene.</p>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Fyll fra disse poolene</legend>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={allPools} onCheckedChange={(v) => setAllPools(v === true)} />
              Alle felles pooler
            </label>
            {!allPools &&
              pools.map((p) => (
                <label key={p.id} className="flex items-center gap-2 pl-6 text-sm">
                  <Checkbox
                    checked={chosen.includes(p.id)}
                    onCheckedChange={(v) => setChosen((c) => (v === true ? [...c, p.id] : c.filter((id) => id !== p.id)))}
                  />
                  {p.name}
                </label>
              ))}
            {booker && booker.own_list_names.length > 0 && (
              <p className="text-xs text-muted-foreground">Egne lister fra arket: {booker.own_list_names.join(", ")}.</p>
            )}
          </fieldset>

          {!valid && <p className="text-sm">Lavvann må være lavere enn høyvann, og høyvann høyst 200.</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              Lagre
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
